import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  XMarkIcon,
  PlusIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { variablesSearchAPI } from '../../services/api';

// Debounce hook for search input
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

const VariableBrowser = ({
  selectedVariables = new Set(),
  disabledVariables = new Set(),
  onVariableSelect,
  onVariablesAdd,
  targetCategory = null,
  showAddToCategory = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [collection, setCollection] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [collections, setCollections] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [localSelected, setLocalSelected] = useState(new Set());

  const debouncedSearch = useDebounce(searchQuery, 300);
  const pageSize = 50;

  // Load collections on mount
  useEffect(() => {
    const loadCollections = async () => {
      try {
        const data = await variablesSearchAPI.getCollections();
        setCollections(data.collections || []);
      } catch (error) {
        console.error('Failed to load collections:', error);
      }
    };
    loadCollections();
  }, []);

  // Search when query or filters change
  useEffect(() => {
    const search = async () => {
      setLoading(true);
      try {
        const data = await variablesSearchAPI.search({
          q: debouncedSearch,
          collection,
          page,
          page_size: pageSize,
        });
        setResults(data.results || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      } catch (error) {
        console.error('Search failed:', error);
        toast.error('Failed to search variables');
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [debouncedSearch, collection, page]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, collection]);

  const handleVariableClick = useCallback((variable) => {
    const varId = variable.id;
    if (showAddToCategory) {
      // Toggle local selection for batch add
      setLocalSelected(prev => {
        const newSet = new Set(prev);
        if (newSet.has(varId)) {
          newSet.delete(varId);
        } else {
          newSet.add(varId);
        }
        return newSet;
      });
    } else if (onVariableSelect) {
      onVariableSelect(variable);
    }
  }, [showAddToCategory, onVariableSelect]);

  const handleAddSelected = useCallback(() => {
    if (localSelected.size === 0) {
      toast.error('No variables selected');
      return;
    }
    if (onVariablesAdd) {
      onVariablesAdd(Array.from(localSelected));
      setLocalSelected(new Set());
      toast.success(`Added ${localSelected.size} variables`);
    }
  }, [localSelected, onVariablesAdd]);

  const isSelected = useCallback((varId) => {
    return selectedVariables.has(varId) || localSelected.has(varId);
  }, [selectedVariables, localSelected]);

  const isDisabled = useCallback((varId) => {
    return disabledVariables.has(varId);
  }, [disabledVariables]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setCollection('');
    setPage(1);
  }, []);

  // Handle select all on current page
  const handleSelectAllOnPage = useCallback(() => {
    const selectableResults = results.filter(v => !isDisabled(v.id));
    if (selectableResults.length === 0) return;

    if (showAddToCategory) {
      // Check if all selectable results are already selected
      const allSelected = selectableResults.every(v => localSelected.has(v.id));
      if (allSelected) {
        // Deselect all on page
        setLocalSelected(prev => {
          const newSet = new Set(prev);
          selectableResults.forEach(v => newSet.delete(v.id));
          return newSet;
        });
        toast.success(`${selectableResults.length} variables deselected`);
      } else {
        // Select all on page
        setLocalSelected(prev => {
          const newSet = new Set(prev);
          selectableResults.forEach(v => newSet.add(v.id));
          return newSet;
        });
        toast.success(`${selectableResults.length} variables selected`);
      }
    } else if (onVariableSelect) {
      // Check if all selectable results are already selected
      const allSelected = selectableResults.every(v => selectedVariables.has(v.id));
      if (allSelected) {
        // Deselect all on page
        selectableResults.forEach(v => onVariableSelect(v));
        toast.success(`${selectableResults.length} variables deselected`);
      } else {
        // Select unselected variables on page
        const unselected = selectableResults.filter(v => !selectedVariables.has(v.id));
        unselected.forEach(v => onVariableSelect(v));
        toast.success(`${unselected.length} variables selected`);
      }
    }
  }, [results, isDisabled, showAddToCategory, localSelected, selectedVariables, onVariableSelect]);

  // Check if all on page are selected
  const allOnPageSelected = useMemo(() => {
    const selectableResults = results.filter(v => !isDisabled(v.id));
    if (selectableResults.length === 0) return false;
    if (showAddToCategory) {
      return selectableResults.every(v => localSelected.has(v.id));
    }
    return selectableResults.every(v => selectedVariables.has(v.id));
  }, [results, isDisabled, showAddToCategory, localSelected, selectedVariables]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      {/* Header - flex-shrink-0 to prevent shrinking */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
          Variable Browser
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({total.toLocaleString()} variables)
          </span>
        </h3>

        {/* Search Input */}
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search variables by name, ID, or description..."
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 pl-10
                       shadow-sm px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          {results.length > 0 && (
            <button
              onClick={handleSelectAllOnPage}
              disabled={loading}
              className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700
                       disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              title={allOnPageSelected ? 'Deselect all on this page' : 'Select all on this page'}
            >
              {allOnPageSelected ? `Deselect ${results.filter(v => !isDisabled(v.id)).length}` : `Select ${results.filter(v => !isDisabled(v.id)).length}`}
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-md border ${
              showFilters || collection
                ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900 dark:border-blue-700 dark:text-blue-200'
                : 'border-gray-300 dark:border-gray-600 text-gray-500'
            }`}
            title="Toggle filters"
          >
            <FunnelIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filters
              </span>
              {(searchQuery || collection) && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Clear all
                </button>
              )}
            </div>
            <select
              value={collection}
              onChange={(e) => setCollection(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600
                       px-3 py-2 text-sm bg-white dark:bg-gray-600
                       text-gray-900 dark:text-white"
            >
              <option value="">All Collections</option>
              {collections.map((coll) => (
                <option key={coll.id} value={coll.id}>
                  {coll.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Add to Category Button */}
        {showAddToCategory && localSelected.size > 0 && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {localSelected.size} selected
            </span>
            <button
              onClick={handleAddSelected}
              className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md
                       hover:bg-green-700 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add to {targetCategory?.name || 'Category'}
            </button>
          </div>
        )}
      </div>

      {/* Results List - flex-1 min-h-0 is critical for scrolling in flex containers */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : results.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            {searchQuery || collection ? 'No variables found' : 'Start typing to search variables'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {results.map((variable) => {
              const selected = isSelected(variable.id);
              const disabled = isDisabled(variable.id);
              return (
                <div
                  key={variable.id}
                  onClick={() => !disabled && handleVariableClick(variable)}
                  className={`p-3 transition-all duration-150 ${
                    disabled
                      ? 'bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed border-l-4 border-l-gray-300 dark:border-l-gray-600'
                      : selected
                        ? 'bg-blue-100 dark:bg-blue-800/50 border-l-4 border-l-blue-600 ring-1 ring-inset ring-blue-300 dark:ring-blue-600 cursor-pointer'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-l-transparent cursor-pointer'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {/* Checkbox indicator */}
                        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          disabled
                            ? 'bg-gray-300 dark:bg-gray-600 border-gray-300 dark:border-gray-600'
                            : selected
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700'
                        }`}>
                          {(selected || disabled) && (
                            <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                          )}
                        </div>
                        <span className={`font-medium truncate ${
                          disabled
                            ? 'text-gray-500 dark:text-gray-400'
                            : selected
                              ? 'text-blue-900 dark:text-blue-100'
                              : 'text-gray-900 dark:text-white'
                        }`}>
                          {variable.alias}
                        </span>
                        {disabled && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">(already added)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 ml-7">
                        ID: {variable.id}
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 ml-7 truncate">
                        {variable.fieldCategory}
                      </div>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                        variable.units === 'pct'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                      }`}>
                        {variable.units === 'pct' ? '%' : variable.units || 'count'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination - flex-shrink-0 to keep at bottom */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 p-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50
                     disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-4 w-4 mr-1" />
            Previous
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className="flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600
                     rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50
                     disabled:cursor-not-allowed"
          >
            Next
            <ChevronRightIcon className="h-4 w-4 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default VariableBrowser;
